import React from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { ChatBubbleLeftEllipsisIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { api } from '@/utils/api';
import { getTitleFromPath } from '@/utils/routeTitles';
import { lerVersao } from '@/utils/versao';

/**
 * ════════════════════════════════════════════════════════════════════════════
 * O CANAL DE RELATO — o formulário
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Quem encontra um defeito hoje não tem como dizer, e o que acontece na prática
 * é que ele para de usar. Este é o caminho entre notar e registrar, e ele
 * precisa ser curto: o que se nota e não se registra, perde-se.
 *
 * ─── O QUE O SISTEMA ANEXA, E POR QUE ELE MOSTRA O QUE ANEXOU ──────────────
 *
 * Tela, rota, papel, escola, plataforma e as DUAS versões — coisas que o
 * professor não saberia informar e sem as quais "não funciona" não é
 * investigável.
 *
 * E o formulário MOSTRA o que vai junto, em vez de anexar em silêncio. Não é
 * cerimônia de privacidade: é o que faz a pessoa confiar no canal. Um
 * formulário que coleta escondido é um formulário que se usa uma vez.
 *
 * ─── O QUE ELE NÃO ANEXA ───────────────────────────────────────────────────
 *
 * Nada de captura de tela, DOM, console ou resposta de aluno. E o risco real
 * não é técnico — é este campo de texto, onde alguém pode escrever "a Maria da
 * 8ºB não conseguiu enviar". Nenhum controle técnico impede isso; o que impede
 * é a linha pedindo para não escrever nome de aluno, que está logo abaixo do
 * campo, e não escondida numa política.
 */

type Tipo = 'PROBLEMA' | 'SUGESTAO' | 'DUVIDA';

const TIPOS: Array<{ valor: Tipo; rotulo: string; ajuda: string }> = [
  { valor: 'PROBLEMA', rotulo: 'Problema', ajuda: 'algo não funcionou como devia' },
  { valor: 'SUGESTAO', rotulo: 'Sugestão', ajuda: 'algo que podia ser melhor' },
  { valor: 'DUVIDA', rotulo: 'Dúvida', ajuda: 'não entendi como se faz' },
];

export const FormularioDeRelato: React.FC<{
  aberto: boolean;
  aoFechar: () => void;
  /** Preenchido quando o relato nasce do limite de erro. */
  erro?: string | null;
}> = ({ aberto, aoFechar, erro = null }) => {
  const { pathname } = useLocation();
  const [tipo, setTipo] = React.useState<Tipo>(erro ? 'PROBLEMA' : 'PROBLEMA');
  const [descricao, setDescricao] = React.useState('');
  const [falha, setFalha] = React.useState<string | null>(null);

  const versao = lerVersao();
  const tela = getTitleFromPath(pathname);

  const m = useMutation({
    mutationFn: async () =>
      (
        await api.post('/api/relatos', {
          tipo,
          descricao: descricao.trim(),
          tela,
          rota: pathname,
          plataforma: versao.plataforma,
          versaoWeb: versao.web,
          versaoNativa: versao.nativo,
          erro,
        })
      ).data as { id: number; avisoPorEmail?: boolean },
    onError: (e: unknown) => {
      const r = (e as { response?: { data?: { error?: string; message?: string } } })?.response;
      setFalha(r?.data?.error ?? r?.data?.message ?? 'Não foi possível enviar. Tente de novo.');
    },
  });

  const fecharELimpar = () => {
    aoFechar();
    // Só limpa depois de fechar, para o texto não sumir na frente de quem
    // apertou fora do quadro por engano e vai reabrir.
    setTimeout(() => { m.reset(); setDescricao(''); setFalha(null); }, 300);
  };

  return (
    <Transition show={aberto} as={React.Fragment}>
      <Dialog onClose={fecharELimpar} className="relative z-50">
        <Transition.Child as={React.Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        </Transition.Child>
        <div className="fixed inset-0 flex items-end justify-center p-0 md:items-center md:p-4">
          <Transition.Child as={React.Fragment} enter="ease-out duration-200" enterFrom="translate-y-4 opacity-0" enterTo="translate-y-0 opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
            <Dialog.Panel className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-white p-5 shadow-xl md:rounded-2xl">
              <div className="flex items-start justify-between gap-3">
                <Dialog.Title className="text-lg font-semibold text-gray-900">
                  {m.isSuccess ? 'Recebido' : 'Falar com quem faz o portal'}
                </Dialog.Title>
                <button type="button" onClick={fecharELimpar} className="-m-1 rounded p-1 text-gray-500 hover:text-gray-700" aria-label="Fechar">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              {m.isSuccess ? (
                <div className="mt-3">
                  <p className="text-sm text-gray-700">
                    Seu relato é o <strong>nº {m.data.id}</strong>.{' '}
                    {/* Achado 209: sem endereço legível, o desfecho não vai por e-mail — o
                        servidor diz qual é o caso, pela mesma regra que decide o envio. */}
                    {m.data.avisoPorEmail === false
                      ? 'Quando eu olhar, a resposta aparece em Minha conta, em “Meus relatos”.'
                      : 'Quando eu olhar, você recebe um e-mail com o que foi feito — não precisa voltar aqui para conferir.'}
                  </p>
                  <p className="mt-2 text-sm text-gray-500">Obrigado. É assim que o portal melhora.</p>
                  <button type="button" onClick={fecharELimpar} className="mt-5 w-full rounded-md bg-marca-600 px-4 py-2 text-sm font-medium text-white hover:bg-marca-700">
                    Fechar
                  </button>
                </div>
              ) : (
                <form
                  className="mt-4"
                  onSubmit={(e) => { e.preventDefault(); setFalha(null); m.mutate(); }}
                >
                  <fieldset>
                    <legend className="text-sm font-medium text-gray-900">O que é?</legend>
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      {TIPOS.map((t) => (
                        <button
                          key={t.valor}
                          type="button"
                          onClick={() => setTipo(t.valor)}
                          aria-pressed={tipo === t.valor}
                          className={`rounded-md border px-2 py-2 text-center text-sm transition ${
                            tipo === t.valor
                              ? 'border-marca-600 bg-marca-50 font-medium text-marca-900'
                              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <span className="block">{t.rotulo}</span>
                          <span className="mt-0.5 block text-[11px] leading-tight text-gray-500">{t.ajuda}</span>
                        </button>
                      ))}
                    </div>
                  </fieldset>

                  <label htmlFor="relato-descricao" className="mt-4 block text-sm font-medium text-gray-900">
                    O que aconteceu?
                  </label>
                  <textarea
                    id="relato-descricao"
                    rows={5}
                    autoFocus
                    value={descricao}
                    onChange={(e) => setDescricao(e.target.value)}
                    placeholder="Cliquei em Gerar PDF e a tela ficou parada em &quot;preparando&quot; por uns cinco minutos."
                    className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm focus:border-marca-500 focus:ring-1 focus:ring-marca-500"
                  />
                  {/* A linha que nenhum controle técnico substitui. */}
                  <p className="mt-1 text-xs text-gray-500">
                    Não é preciso escrever nome de aluno.
                  </p>

                  <details className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-3">
                    <summary className="cursor-pointer text-xs font-medium text-gray-600">
                      O que vai junto automaticamente
                    </summary>
                    <dl className="mt-2 space-y-0.5 text-xs text-gray-600">
                      <div><dt className="inline font-medium">Tela: </dt><dd className="inline">{tela} ({pathname})</dd></div>
                      <div><dt className="inline font-medium">Onde: </dt><dd className="inline">{versao.plataforma === 'web' ? 'navegador' : `aplicativo (${versao.plataforma})`}</dd></div>
                      <div><dt className="inline font-medium">Versão: </dt><dd className="inline font-mono">{versao.web}{versao.nativo ? ` · app ${versao.nativo}` : ''}</dd></div>
                      {erro && <div><dt className="inline font-medium">Erro: </dt><dd className="inline font-mono break-words">{erro}</dd></div>}
                      <div className="pt-1 text-gray-500">Seu nome, seu papel e sua escola. Nada de aluno, e nenhuma foto da tela.</div>
                    </dl>
                  </details>

                  {falha && <p className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">{falha}</p>}

                  <button
                    type="submit"
                    disabled={m.isPending || descricao.trim().length < 10}
                    className="mt-4 w-full rounded-md bg-marca-600 px-4 py-2 text-sm font-medium text-white hover:bg-marca-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {m.isPending ? 'Enviando…' : 'Enviar'}
                  </button>
                </form>
              )}
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

/** O botão que abre o formulário. Um estado, para quem só quer pendurá-lo. */
export const BotaoDeRelato: React.FC<{
  className?: string;
  rotulo?: string;
  erro?: string | null;
  children?: React.ReactNode;
}> = ({ className, rotulo = 'Relatar algo', erro = null, children }) => {
  const [aberto, setAberto] = React.useState(false);
  return (
    <>
      <button type="button" onClick={() => setAberto(true)} className={className}>
        {children ?? (
          <>
            <ChatBubbleLeftEllipsisIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
            <span>{rotulo}</span>
          </>
        )}
      </button>
      <FormularioDeRelato aberto={aberto} aoFechar={() => setAberto(false)} erro={erro} />
    </>
  );
};
